import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ObscuredData } from '@se/components';
import { VisitExamination } from '@se/models';
import {
    Addendum,
    AuthData,
    EcgChartState,
    IReportWorkflowExamService,
    IResponseStatus,
    SearchFormType,
    SeSignInfo,
    SissData,
    UnlockData,
} from '@se/platform';
import { ServerRootService, TokenStoreInjector, TokenStoreService } from '@se/se.ui.html5';
import { SignType } from 'estensa-module';
import { HardCodedDataUpdate, SissUploadDocumentData } from './report-workflow-exam.service';
import { ISeSignResultData, SeMultiSignInfo } from '../sign/estensasign.model';
import { ISpecialtyExamination } from '../models/specialty';

@Injectable({
    providedIn: 'root',
})
export class CardiologyVisitRestService extends ServerRootService implements IReportWorkflowExamService {
    constructor(
        @Inject(TokenStoreInjector) public tokenStore: TokenStoreService,
        private httpClient: HttpClient,
    ) {
        super();
    }

    /**
     * per il multi approve dal secondo all'ennesimo refertatore va a leggere otherCredentialsData
     * @param reportIds
     * @param authData
     * @param isDeferred
     * @param isFromWorklist
     * @param displayedSettings
     * @param otherCredentialsData
     */
    approve(
        reportIds: string[],
        authData?: AuthData,
        isDeferred = false,
        isFromWorklist?: boolean,
        displayedSettings?: EcgChartState,
        otherCredentialsData?: AuthData[],
        referringPhysicianId?: string
    ) {
        let otherCredentials: any[] = [];
        if (otherCredentialsData && otherCredentialsData.length >= 1) {
            otherCredentialsData.forEach((credential) => {
                otherCredentials.push(this.encodeBase64(credential));
            });
        }

        return this.httpClient.post<IResponseStatus[]>(
            this.getServerRoot() + '/Visit/Approve',
            { reportIds, isDeferred, isFromWorklist, displayedSettings, otherCredentials, referringPhysicianId },
            this.buildOptions(authData),
        );
    }

    undoValidation(reportId: string, authData: AuthData, isFromWorklist?: boolean) {
        return this.httpClient.post(
            this.getServerRoot() + '/Visit/UndoApprove',
            { reportId, isFromWorklist },
            this.buildOptions(authData),
        );
    }

    undoMultiValidation(reportId: string, authData: AuthData[], isFromWorklist?: boolean) {
        const otherCredentials = authData.slice(1).map((credentials) => this.encodeBase64(credentials));
        return this.httpClient.post<IResponseStatus>(
            this.getServerRoot() + '/Visit/UndoApprove',
            { reportId, isFromWorklist, otherCredentials },
            this.buildOptions(authData[0]),
        );
    }

    deleteExaminations(ids: string[]) {
        return this.httpClient.put(this.getServerRoot() + '/Visit/DeleteExaminations', ids, this.buildOptions());
    }

    forceUnlockMultiple(data: UnlockData[]) {
        return this.httpClient.put(this.getServerRoot() + '/Visit/ForceUnlockMultiple', data, this.buildOptions());
    }

    unlock(data: UnlockData[]) {
        return this.httpClient.put(this.getServerRoot() + '/Visit/Unlock', data, this.buildOptions());
    }

    getDataForSissSign(examinationIds: any) {
        return this.httpClient.get<SissData>(
            this.getServerRoot() + '/ECG/GetSissData/?examinationId=' + examinationIds,
            this.buildOptions(),
        );
    }

    uploadReportToSissCloud(data: SissUploadDocumentData) {
        return this.httpClient.post<ISeSignResultData[]>(
            `${this.getServerRoot()}/Visit/UploadReportToSissCloud`,
            {
                type: SignType.SISSCloud,
                sissCloud: {
                    reportIdList: data.reportIds,
                    signerId: data.signerId,
                    signerUsername: data.signerUsername,
                    token: data.token,
                },
            },
            this.buildOptions(),
        );
    }

    uploadReportToSissWayCloud(data: SissUploadDocumentData) {
        return this.httpClient.post<ISeSignResultData[]>(
            `${this.getServerRoot()}/Visit/UploadReportToSissWayCloud`,
            {
                type: SignType.SISSWayCloud,
                sissWayCloud: {
                    reportIdList: data.reportIds,
                    signerId: data.signerId,
                    signerUsername: data.signerUsername,
                    token: data.token,
                    reportDocumentiList: data.reportDocumentiList,
                    idStruttura: data.idStruttura,
                    idSubStruttura: data.idSubStruttura,
                    codBrancaSpecialistica: data.codBrancaSpecialistica,
                },
            },
            this.buildOptions(),
        );
    }

    getPdf(info: SeSignInfo) {
        return this.httpClient.post(this.getServerRoot() + '/Visit/GetPdfVisitReport', info, this.buildOptions());
    }

    getReportExaminations(idReport: string) {
        return this.httpClient.post<VisitExamination[]>(
            this.getServerRoot() + '/Visit/GetReportExaminations',
            { idReport },
            this.buildOptions(),
        );
    }

    getReportSpecialtyExaminations(idReport: string) {
        return this.httpClient.post<ISpecialtyExamination[]>(
            this.getServerRoot() + '/Visit/GetReportExaminations',
            { idReport },
            this.buildOptions(),
        );
    }

    lockExams(examinations: VisitExamination[]) {
        return this.httpClient.post(this.getServerRoot() + '/Visit/LockExams', examinations, this.buildOptions());
    }

    merge(formType: SearchFormType, source: string, target: string) {
        const params = {
            IdExaminationSource: source,
            IdExaminationTarget: target,
        };
        return this.httpClient.post(
            this.getServerRoot() + '/' + formType + '/MergeReport',
            params,
            this.buildOptions(),
        );
    }

    undoMerge(formType: SearchFormType, examinationId: string) {
        return this.httpClient.post(
            this.getServerRoot() + '/' + formType + '/UndoMergeReport',
            JSON.stringify(examinationId),
            this.buildOptions(),
        );
    }

    sign(info: SeSignInfo) {
        return this.httpClient.post<ISeSignResultData[]>(
            this.getServerRoot() + '/Visit/SignReport',
            info,
            this.buildOptions(),
        );
    }

    /*83782*/
    multiSign(info: SeMultiSignInfo) {
        return this.httpClient.post<ISeSignResultData[]>(
            this.getServerRoot() + '/Visit/MultiSignReport',
            info,
            this.buildOptions(),
        );
    }

    getFenixToken(patientId: string, provenanceId: string) {
        return this.httpClient.post<string>(
            this.getServerRoot() + '/FenixDossier/GetToken',
            { PatientId: patientId, IdProvenance: provenanceId },
            this.buildOptions(),
        );
    }

    burnCd(idExaminations: string[]) {
        return this.httpClient.post<string>(
            this.getServerRoot() + '/MPS/BurnCDVNext',
            { burner: null, clientName: '', mediaType: '', idExaminations },
            this.buildOptions(),
        );
    }

    saveAddendum(data: Addendum) {
        return this.httpClient.post<Addendum>(this.getServerRoot() + '/Visit/SaveAddendum', data, this.buildOptions());
    }

    deleteAddendum(data: Addendum) {
        return this.httpClient.post<Addendum>(
            this.getServerRoot() + '/Visit/DeleteAddendum',
            data,
            this.buildOptions(),
        );
    }

    saveHardCodedData(update: HardCodedDataUpdate) {
        return this.httpClient.post<ObscuredData>(
            this.getServerRoot() + '/Visit/SaveHardCodedData',
            update,
            this.buildOptions(),
        );
    }

    suspend (idExaminations : string[], idSuspensionReason : string) {
        return this.httpClient.post(this.getServerRoot() + '/Workflow/Suspend', { idExaminations, idSuspensionReason }, this.buildOptions());
    }

    undoSuspension (idExaminations : string[]) {
        return this.httpClient.post(this.getServerRoot() + '/Workflow/UndoSuspension', { idExaminations }, this.buildOptions());
    }
}
